import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ApiClient from '../utils/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, X, Globe, Eye, Upload, Camera, Loader2,
  FileText, Award, FileCheck, Lock, Download, QrCode, Printer,
  Calendar, Clock, MapPin, CheckCircle, AlertCircle
} from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PhoneInput from '../components/PhoneInput';
import { QRCodeSVG } from 'qrcode.react';
import { LANGUAGES, getLanguageName } from '../utils/languages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VENDOR_TYPES = [
  { value: 'interpreter', label: 'Interpreter' },
  { value: 'translator', label: 'Translator' },
  { value: 'both', label: 'Both (Interpreter & Translator)' },
];

const MODALITIES = [
  { value: 'onsite', label: 'On-site' },
  { value: 'vri', label: 'VRI (Video Remote)' },
  { value: 'opi', label: 'OPI (Phone)' },
];

const SPECIALTIES = ['medical', 'legal', 'business', 'technical', 'education', 'general'];

// Service categories for vendor certification
const SERVICE_CATEGORY_OPTIONS = [
  { value: 'legal', label: 'Legal (General)' },
  { value: 'legal.court', label: 'Legal - Court' },
  { value: 'legal.deposition', label: 'Legal - Depositions' },
  { value: 'legal.other_legal', label: 'Legal - Other' },
  { value: 'medical', label: 'Medical' },
  { value: 'medical.clinical', label: 'Medical - Clinical' },
  { value: 'medical.pharmaceutical', label: 'Medical - Pharmaceutical' },
  { value: 'technical', label: 'Technical' },
  { value: 'general', label: 'General' },
];

const VendorForm = () => {
  const { vendorId } = useParams();
  const isEdit = !!vendorId;
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(null);
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const [recentJobs, setRecentJobs] = useState([]);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    business_name: '',
    email: '',
    phone: '',
    address: '',
    address_line2: '',
    vendor_type: 'both',
    languages: [],
    language_pairs: [],
    modalities: [],
    specialties: [],
    service_categories: [],
    credentials: [],
    rate_plan: [],
    w9_on_file: false,
    nda_on_file: false,
    baa_on_file: false,
    direct_deposit_on_file: false,
    certification_on_file: false,
    photo_on_file: false,
    notes: '',
    documents: {
      resume: null,
      certification: null,
      baa: null,
      contract: null,
      w9: null,
      nda: null,
      direct_deposit: null,
      photo: null
    },
    profile_settings: {
      public_profile_enabled: false,
      profile_slug: '',
      profile_photo_url: null,
      bio: '',
      tagline: '',
      city: '',
      availability: '',
      years_experience: null,
      favorite_assignments: '',
      custom_message: '',
      show_languages: true,
      show_specialties: true,
      show_credentials: false,
      show_modalities: true
    }
  });

  // Document types configuration
  const DOCUMENT_TYPES = [
    { key: 'w9', label: 'W-9 Tax Form', icon: 'FileText', required: true, description: 'Required for payment processing' },
    { key: 'direct_deposit', label: 'Direct Deposit Form', icon: 'FileText', required: true, description: 'Bank account information for payments' },
    { key: 'nda', label: 'NDA (Non-Disclosure Agreement)', icon: 'Lock', required: true, description: 'Confidentiality agreement' },
    { key: 'baa', label: 'BAA (Business Associate Agreement)', icon: 'FileCheck', required: true, description: 'HIPAA compliance for healthcare assignments' },
    { key: 'certification', label: 'Interpreter/Translator Certification', icon: 'Award', required: false, description: 'Professional certifications (ATA, CCHI, NBCMI, etc.)' },
    { key: 'photo', label: 'ID Badge Photo', icon: 'Camera', required: false, description: 'Professional headshot for ID badge' },
    { key: 'resume', label: 'Resume/CV', icon: 'FileText', required: false, description: 'Professional background' },
    { key: 'contract', label: 'Signed Contract', icon: 'FileCheck', required: false, description: 'Independent contractor agreement' }
  ];

  // For adding new language pairs
  const [newPair, setNewPair] = useState({
    source_language: 'en',
    target_language: '',
    service_type: 'both'
  });

  // Helper function to update profile settings without stale closures
  const updateProfileSettings = (key, value) => {
    setFormData(prev => ({
      ...prev,
      profile_settings: { ...prev.profile_settings, [key]: value }
    }));
  };

  // Helper function to update documents
  const updateDocument = (key, value) => {
    setFormData(prev => ({
      ...prev,
      documents: { ...prev.documents, [key]: value }
    }));
  };

  useEffect(() => {
    if (isEdit) {
      fetchVendor();
      fetchRecentJobs();
    }
  }, [vendorId]);

  const fetchVendor = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vendors/${vendorId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Ensure documents object exists
        if (!data.documents) {
          data.documents = { resume: null, certification: null, baa: null, contract: null, w9: null, nda: null };
        }
        setFormData(data);
      }
    } catch (error) {
      toast.error('Failed to load vendor');
    }
  };

  const fetchRecentJobs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vendors/${vendorId}/assignments?limit=5`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRecentJobs(data.assignments || data || []);
      }
    } catch (error) {
      console.error('Failed to load recent jobs:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPG, PNG, WebP, or GIF image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingPhoto(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      // Get auth headers but remove Content-Type for FormData
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      
      const response = await fetch(`${API_URL}/api/vendors/${vendorId}/upload-photo`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: uploadFormData
      });

      if (response.ok) {
        const data = await response.json();
        setFormData({
          ...formData,
          profile_settings: { 
            ...formData.profile_settings, 
            profile_photo_url: data.photo_url 
          }
        });
        toast.success('Photo uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload photo');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      const response = await fetch(`${API_URL}/api/vendors/${vendorId}/photo`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        setFormData({
          ...formData,
          profile_settings: { 
            ...formData.profile_settings, 
            profile_photo_url: null 
          }
        });
        toast.success('Photo deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete photo');
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  // Document upload handler
  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, DOC, DOCX, JPG, or PNG.');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    
    setUploadingDocument(docType);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('document_type', docType);
    
    try {
      const headers = { ...getAuthHeaders() };
      delete headers['Content-Type'];
      
      const response = await fetch(`${API_URL}/api/vendors/${vendorId}/documents`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: uploadFormData
      });
      
      if (response.ok) {
        const data = await response.json();
        updateDocument(docType, {
          url: data.document_url,
          filename: file.name,
          uploaded_at: new Date().toISOString()
        });
        toast.success(`${docType.toUpperCase()} uploaded successfully`);
        
        // Update the on_file flags for all document types
        const onFileFlags = {
          w9: 'w9_on_file',
          nda: 'nda_on_file',
          baa: 'baa_on_file',
          direct_deposit: 'direct_deposit_on_file',
          certification: 'certification_on_file',
          photo: 'photo_on_file'
        };
        if (onFileFlags[docType]) {
          setFormData(prev => ({ ...prev, [onFileFlags[docType]]: true }));
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to upload ${docType}`);
      }
    } catch (error) {
      toast.error(`Failed to upload ${docType}`);
    } finally {
      setUploadingDocument(null);
    }
  };

  // State for document viewer modal
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewerFilename, setViewerFilename] = useState('');
  const [viewerType, setViewerType] = useState('');

  // View document - opens in modal viewer for PDFs/images, downloads for others
  const handleViewDocument = async (documentUrl, filename) => {
    try {
      if (!documentUrl) {
        toast.error('Document URL is missing');
        return;
      }
      
      // Build the correct API URL for document fetch
      let fetchUrl;
      
      if (documentUrl.startsWith('http://') || documentUrl.startsWith('https://')) {
        fetchUrl = documentUrl;
      } else if (documentUrl.startsWith('/api/vendors/documents/')) {
        fetchUrl = `${API_URL}${documentUrl}`;
      } else if (documentUrl.startsWith('/api/')) {
        fetchUrl = `${API_URL}${documentUrl}`;
      } else {
        const urlFilename = documentUrl.split('/').pop();
        fetchUrl = `${API_URL}/api/vendors/documents/${urlFilename}`;
      }
      
      console.log('[Document View] Fetching:', fetchUrl);
      
      const response = await fetch(fetchUrl, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      console.log('[Document View] Response status:', response.status);
      
      if (response.ok) {
        const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        // Determine file type
        const ext = (filename || '').split('.').pop()?.toLowerCase();
        const isPdf = contentType.includes('pdf') || ext === 'pdf';
        const isImage = contentType.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
        
        if (isPdf || isImage) {
          // Open in modal viewer
          setViewerUrl(blobUrl);
          setViewerFilename(filename || 'Document');
          setViewerType(isPdf ? 'pdf' : 'image');
          setViewerOpen(true);
        } else {
          // Download non-viewable files
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename || 'document';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        }
      } else {
        let errorMsg = 'Failed to load document';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          errorMsg = `Failed to load document (${response.status})`;
        }
        console.error('[Document View] Error:', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('[Document View] Exception:', error);
      toast.error('Failed to load document: ' + (error.message || 'Network error'));
    }
  };

  // Close document viewer and clean up
  const closeViewer = () => {
    if (viewerUrl) {
      URL.revokeObjectURL(viewerUrl);
    }
    setViewerOpen(false);
    setViewerUrl(null);
    setViewerFilename('');
    setViewerType('');
  };

  // Handle profile photo upload
  const photoInputRef = useRef(null);

  // Get public profile URL
  const getPublicProfileUrl = () => {
    const slug = formData.profile_settings?.profile_slug || vendorId;
    return `${window.location.origin}/interpreter/${slug}`;
  };

  // Print ID badge
  const handlePrintBadge = () => {
    const printWindow = window.open('', '_blank');
    const badgeHtml = generateBadgeHtml();
    printWindow.document.write(badgeHtml);
    printWindow.document.close();
    printWindow.print();
  };

  // Generate badge HTML for printing - New design inspired by sample
  const generateBadgeHtml = () => {
    const photoUrl = getPhotoUrl(formData.profile_settings?.profile_photo_url);
    const profileUrl = getPublicProfileUrl();
    const firstName = formData.first_name || 'Interpreter';
    const lastName = formData.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bylyngo ID Badge - ${fullName}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Inter', sans-serif; background: #f0f0f0; padding: 20px; }
          .badge-container { display: flex; gap: 20px; }
          .badge { 
            width: 2.125in; height: 3.375in; 
            background: #0027A9;
            border-radius: 16px;
            position: relative; 
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          }
          /* Decorative curve */
          .curve {
            position: absolute;
            top: 50%;
            left: -20%;
            width: 140%;
            height: 60%;
            background: #FFD700;
            border-radius: 50%;
            transform: rotate(-15deg);
          }
          /* Orange accent circle */
          .accent-circle {
            position: absolute;
            top: 35%;
            right: -10%;
            width: 60px;
            height: 60px;
            background: #FF6600;
            border-radius: 50%;
          }
          /* Logo section */
          .logo-section {
            position: absolute;
            top: 16px;
            left: 16px;
            z-index: 10;
          }
          .logo-icon {
            width: 32px;
            height: 32px;
            background: #FFD700;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 800;
            color: #0027A9;
            font-size: 18px;
            margin-bottom: 4px;
          }
          .logo-text {
            font-size: 14px;
            font-weight: 700;
            color: #FFD700;
          }
          .logo-subtext {
            font-size: 8px;
            color: white;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          /* ID Number strip */
          .id-strip {
            position: absolute;
            top: 16px;
            right: 8px;
            writing-mode: vertical-rl;
            text-orientation: mixed;
            background: #FFD700;
            color: #0027A9;
            padding: 8px 6px;
            border-radius: 12px;
            font-weight: 700;
            font-size: 10px;
            z-index: 10;
          }
          /* Photo */
          .photo-container {
            position: absolute;
            top: 80px;
            right: 16px;
            z-index: 10;
          }
          .photo {
            width: 90px;
            height: 90px;
            border-radius: 50%;
            background: #e5e7eb;
            object-fit: cover;
            border: 4px solid #FFD700;
          }
          /* Name & Title */
          .info-section {
            position: absolute;
            left: 16px;
            top: 95px;
            z-index: 10;
            max-width: 55%;
          }
          .last-name {
            font-size: 18px;
            font-weight: 800;
            color: #FFD700;
            text-transform: uppercase;
            line-height: 1.1;
          }
          .first-name {
            font-size: 14px;
            font-weight: 700;
            color: #FFD700;
            text-transform: uppercase;
          }
          .title {
            font-size: 10px;
            font-weight: 600;
            color: #FFD700;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-top: 6px;
          }
          /* Bottom section */
          .bottom-section {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: #FFD700;
            padding: 12px 16px;
            z-index: 10;
          }
          .company-name {
            font-size: 12px;
            font-weight: 800;
            color: #0027A9;
            text-transform: uppercase;
          }
          .website {
            font-size: 9px;
            color: white;
            margin-top: 2px;
          }
          /* QR Code */
          .qr-section {
            position: absolute;
            bottom: 60px;
            right: 16px;
            z-index: 10;
            text-align: center;
          }
          .qr-code {
            background: white;
            padding: 4px;
            border-radius: 6px;
          }
          .qr-label {
            font-size: 7px;
            color: white;
            margin-top: 4px;
          }
          /* Arrow decoration */
          .arrow {
            position: absolute;
            bottom: 70px;
            left: 16px;
            width: 24px;
            height: 24px;
            color: #FFD700;
            z-index: 10;
          }
          @media print {
            body { background: white; padding: 0; }
            .badge { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="badge">
          <div class="curve"></div>
          <div class="accent-circle"></div>
          
          <div class="logo-section">
            <div class="logo-icon">B</div>
            <div class="logo-text">Bylyngo</div>
            <div class="logo-subtext">Interpreting Services</div>
          </div>
          
          <div class="id-strip">No. ${formData.vendor_number || 'V-XXXXX'}</div>
          
          <div class="photo-container">
            ${photoUrl ? `<img class="photo" src="${photoUrl}" alt="Photo" />` : '<div class="photo"></div>'}
          </div>
          
          <div class="info-section">
            <div class="last-name">${lastName || firstName}</div>
            ${lastName ? `<div class="first-name">${firstName}</div>` : ''}
            <div class="title">Certified Interpreter</div>
          </div>
          
          <div class="qr-section">
            <div class="qr-code">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=45x45&data=${encodeURIComponent(profileUrl)}" width="45" height="45" />
            </div>
            <div class="qr-label">Scan to verify</div>
          </div>
          
          <svg class="arrow" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
          </svg>
          
          <div class="bottom-section">
            <div class="company-name">Bylyngo Interpreting Services</div>
            <div class="website">www.bylyngo.com</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Client-side validation
    if (!formData.first_name || !formData.first_name.trim()) {
      toast.error('First name is required');
      setLoading(false);
      return;
    }
    
    if (!formData.last_name || !formData.last_name.trim()) {
      toast.error('Last name is required');
      setLoading(false);
      return;
    }
    
    if (!formData.email || !formData.email.trim()) {
      toast.error('Email address is required');
      setLoading(false);
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      setLoading(false);
      return;
    }
    
    try {
      const url = isEdit 
        ? `/api/vendors/${vendorId}` 
        : `/api/vendors`;
      
      const headers = getAuthHeaders();
      const result = isEdit 
        ? await ApiClient.put(url, formData, headers)
        : await ApiClient.post(url, formData, headers);
      
      if (result.ok) {
        toast.success(isEdit ? 'Vendor updated successfully!' : 'Vendor created successfully!');
        navigate('/vendors');
      } else {
        // Log the full error for debugging
        console.error('Vendor save failed:', result.status, result.data);
        
        // Parse and display errors
        if (result.data?.detail) {
          if (Array.isArray(result.data.detail)) {
            // Pydantic validation errors - show each one
            result.data.detail.forEach(err => {
              const field = err.loc ? err.loc.slice(1).join('.') : 'Field';
              const message = `${field}: ${err.msg}`;
              toast.error(message, { duration: 8000 });
              console.error('Validation error:', message);
            });
          } else if (typeof result.data.detail === 'string') {
            toast.error(result.data.detail, { duration: 8000 });
          } else {
            toast.error('Validation failed. Check required fields.', { duration: 8000 });
          }
        } else if (result.data?.message) {
          toast.error(result.data.message, { duration: 8000 });
        } else {
          toast.error(`Failed to save vendor (Error ${result.status})`, { duration: 8000 });
        }
      }
    } catch (error) {
      console.error('Vendor save error:', error);
      toast.error(`Failed to save vendor: ${error.message || 'Network error'}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = (code) => {
    setFormData({
      ...formData,
      languages: formData.languages.includes(code)
        ? formData.languages.filter(l => l !== code)
        : [...formData.languages, code]
    });
  };

  const toggleModality = (value) => {
    setFormData({
      ...formData,
      modalities: formData.modalities.includes(value)
        ? formData.modalities.filter(m => m !== value)
        : [...formData.modalities, value]
    });
  };

  const toggleSpecialty = (value) => {
    setFormData({
      ...formData,
      specialties: formData.specialties.includes(value)
        ? formData.specialties.filter(s => s !== value)
        : [...formData.specialties, value]
    });
  };

  const addLanguagePair = () => {
    if (!newPair.target_language || newPair.source_language === newPair.target_language) {
      toast.error('Please select a valid target language');
      return;
    }
    
    // Check for duplicate pair
    const exists = (formData.language_pairs || []).some(
      p => p.source_language === newPair.source_language && 
           p.target_language === newPair.target_language &&
           p.service_type === newPair.service_type
    );
    
    if (exists) {
      toast.error('This language pair already exists');
      return;
    }
    
    setFormData({
      ...formData,
      language_pairs: [...(formData.language_pairs || []), { ...newPair }]
    });
    setNewPair({ source_language: 'en', target_language: '', service_type: 'both' });
  };

  const removeLanguagePair = (index) => {
    setFormData({
      ...formData,
      language_pairs: formData.language_pairs.filter((_, i) => i !== index)
    });
  };

  // Using centralized getLanguageName from ../utils/languages.js

  const getServiceTypeLabel = (type) => {
    switch(type) {
      case 'interpreter': return 'Interpreting';
      case 'translator': return 'Translation';
      case 'both': return 'Both';
      default: return type;
    }
  };

  const addCredential = () => {
    setFormData({
      ...formData,
      credentials: [...formData.credentials, { name: '', issuer: '', issue_date: '', expiry_date: '' }]
    });
  };

  const removeCredential = (index) => {
    setFormData({
      ...formData,
      credentials: formData.credentials.filter((_, i) => i !== index)
    });
  };

  const updateCredential = (index, field, value) => {
    const credentials = [...formData.credentials];
    credentials[index] = { ...credentials[index], [field]: value };
    setFormData({ ...formData, credentials });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="vendor-form">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/vendors')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold font-['Manrope']">
          {isEdit ? 'Edit Vendor' : 'New Vendor'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="vendor-first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="vendor-last-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  id="business_name"
                  value={formData.business_name || ''}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="If applicable"
                  data-testid="vendor-business-name-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="vendor-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <PhoneInput
                  id="phone"
                  value={formData.phone}
                  onChange={(value) => setFormData(prev => ({ ...prev, phone: value }))}
                  data-testid="vendor-phone-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor Type *</Label>
                <select
                  value={formData.vendor_type || 'both'}
                  onChange={(e) => setFormData({ ...formData, vendor_type: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  data-testid="vendor-type-select"
                >
                  {VENDOR_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <AddressAutocomplete
                id="address"
                value={formData.address || ''}
                value2={formData.address_line2 || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, address: value }))}
                onChange2={(value) => setFormData(prev => ({ ...prev, address_line2: value }))}
                placeholder="Enter vendor address"
                placeholder2="Suite #, cross street, or other details"
                showLine2={true}
                data-testid="vendor-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Language Pairs */}
        <Card>
          <CardHeader>
            <CardTitle>Language Pairs</CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Define the language pairs this vendor can work with and what service type for each pair
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Existing language pairs */}
            {(formData.language_pairs || []).length > 0 && (
              <div className="space-y-2">
                {formData.language_pairs.map((pair, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{getLanguageName(pair.source_language)}</span>
                      <span className="text-slate-400">↔</span>
                      <span className="font-medium">{getLanguageName(pair.target_language)}</span>
                      <Badge variant={
                        pair.service_type === 'interpreter' ? 'default' : 
                        pair.service_type === 'translator' ? 'secondary' : 'outline'
                      }>
                        {getServiceTypeLabel(pair.service_type)}
                      </Badge>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLanguagePair(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new language pair */}
            <div className="flex flex-wrap items-end gap-3 p-4 border-2 border-dashed rounded-lg bg-slate-50/50">
              <div className="space-y-1">
                <Label className="text-xs">Source Language</Label>
                <select
                  value={newPair.source_language}
                  onChange={(e) => setNewPair({ ...newPair, source_language: e.target.value })}
                  className="h-9 px-3 rounded-md border border-input bg-white text-sm"
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
              
              <span className="text-slate-400 pb-2">↔</span>
              
              <div className="space-y-1">
                <Label className="text-xs">Target Language</Label>
                <select
                  value={newPair.target_language}
                  onChange={(e) => setNewPair({ ...newPair, target_language: e.target.value })}
                  className="h-9 px-3 rounded-md border border-input bg-white text-sm"
                >
                  <option value="">Select language...</option>
                  {LANGUAGES.filter(l => l.code !== newPair.source_language).map((lang) => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Service Type</Label>
                <select
                  value={newPair.service_type}
                  onChange={(e) => setNewPair({ ...newPair, service_type: e.target.value })}
                  className="h-9 px-3 rounded-md border border-input bg-white text-sm"
                >
                  <option value="both">Both</option>
                  <option value="interpreter">Interpreting Only</option>
                  <option value="translator">Translation Only</option>
                </select>
              </div>
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLanguagePair}
                className="h-9"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Pair
              </Button>
            </div>
            
            {(formData.language_pairs || []).length === 0 && (
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                Add at least one language pair to define what languages this vendor can work with.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Skills & Modalities */}
        <Card>
          <CardHeader>
            <CardTitle>Skills & Modalities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Modalities - only show for interpreters */}
            {(formData.vendor_type === 'interpreter' || formData.vendor_type === 'both') && (
              <div className="space-y-3">
                <Label>Interpreting Modalities</Label>
                <div className="flex flex-wrap gap-2">
                  {MODALITIES.map((mod) => (
                    <Badge
                      key={mod.value}
                      variant={formData.modalities.includes(mod.value) ? 'default' : 'outline'}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => toggleModality(mod.value)}
                    >
                      {mod.label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Label>Specialties</Label>
              <div className="flex flex-wrap gap-2">
                {SPECIALTIES.map((spec) => (
                  <Badge
                    key={spec}
                    variant={formData.specialties.includes(spec) ? 'secondary' : 'outline'}
                    className="cursor-pointer capitalize hover:opacity-80 transition-opacity"
                    onClick={() => toggleSpecialty(spec)}
                  >
                    {spec}
                    {formData.specialties.includes(spec) && (
                      <X className="h-3 w-3 ml-1" />
                    )}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Service Category Certifications</Label>
              <p className="text-sm text-muted-foreground">Select categories this vendor is certified to handle</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {SERVICE_CATEGORY_OPTIONS.map((cat) => (
                  <div key={cat.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`cat-${cat.value}`}
                      checked={formData.service_categories?.includes(cat.value)}
                      onCheckedChange={(checked) => {
                        const current = formData.service_categories || [];
                        setFormData({
                          ...formData,
                          service_categories: checked
                            ? [...current, cat.value]
                            : current.filter(c => c !== cat.value)
                        });
                      }}
                    />
                    <Label htmlFor={`cat-${cat.value}`} className="cursor-pointer text-sm">{cat.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance */}
        <Card>
          <CardHeader>
            <CardTitle>Compliance Documents</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <Label htmlFor="w9">W-9 on File</Label>
                <Switch
                  id="w9"
                  checked={formData.w9_on_file}
                  onCheckedChange={(checked) => setFormData({ ...formData, w9_on_file: checked })}
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <Label htmlFor="nda">NDA on File</Label>
                <Switch
                  id="nda"
                  checked={formData.nda_on_file}
                  onCheckedChange={(checked) => setFormData({ ...formData, nda_on_file: checked })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credentials */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Credentials & Certifications</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addCredential}>
              <Plus className="h-4 w-4 mr-2" /> Add Credential
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.credentials.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No credentials added</p>
            ) : (
              formData.credentials.map((cred, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Credential {index + 1}</span>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeCredential(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      placeholder="Certification Name"
                      value={cred.name}
                      onChange={(e) => updateCredential(index, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Issuing Organization"
                      value={cred.issuer}
                      onChange={(e) => updateCredential(index, 'issuer', e.target.value)}
                    />
                    <div className="space-y-1">
                      <Label className="text-xs">Issue Date</Label>
                      <Input
                        type="date"
                        value={cred.issue_date}
                        onChange={(e) => updateCredential(index, 'issue_date', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Expiry Date</Label>
                      <Input
                        type="date"
                        value={cred.expiry_date}
                        onChange={(e) => updateCredential(index, 'expiry_date', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Public Profile Settings - Only show for edit mode */}
        {isEdit && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Public Profile
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Create a shareable profile page for this vendor (no sensitive info exposed)
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border bg-blue-50">
                <div>
                  <Label htmlFor="public_profile" className="font-medium">Enable Public Profile</Label>
                  <p className="text-sm text-muted-foreground">Allow this profile to be viewed publicly</p>
                </div>
                <Switch
                  id="public_profile"
                  checked={formData.profile_settings?.public_profile_enabled || false}
                  onCheckedChange={(checked) => updateProfileSettings('public_profile_enabled', checked)}
                />
              </div>

              {formData.profile_settings?.public_profile_enabled && (
                <div className="space-y-4 pt-4 border-t">
                  {/* Public Profile URL */}
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <Label className="text-sm font-medium text-green-800">Public Profile URL</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-sm bg-white px-2 py-1 rounded border flex-1 overflow-x-auto">
                        {window.location.origin}/vendor-profile/{formData.profile_settings?.profile_slug || '[enter-slug]'}
                      </code>
                      {formData.profile_settings?.profile_slug && (
                        <a
                          href={`/vendor-profile/${formData.profile_settings.profile_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800"
                        >
                          <Eye className="h-5 w-5" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Profile Photo */}
                  <div className="space-y-2">
                    <Label className="font-semibold">Profile Photo</Label>
                    <div className="flex items-start gap-4">
                      <div className="relative w-28 h-28 rounded-xl overflow-hidden bg-slate-100 border-2 border-slate-200 flex-shrink-0 group">
                        {formData.profile_settings?.profile_photo_url ? (
                          <img 
                            src={getPhotoUrl(formData.profile_settings.profile_photo_url)} 
                            alt="Profile" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
                            <Camera className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                        {uploadingPhoto && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-white animate-spin" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        {isEdit ? (
                          <>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handlePhotoUpload}
                              className="hidden"
                              id="photo-upload"
                            />
                            <div className="flex gap-2">
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingPhoto}
                                className="gap-2"
                              >
                                {uploadingPhoto ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4" />
                                )}
                                {formData.profile_settings?.profile_photo_url ? 'Change Photo' : 'Upload Photo'}
                              </Button>
                              {formData.profile_settings?.profile_photo_url && (
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={handleDeletePhoto}
                                  className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Remove
                                </Button>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Upload a JPG, PNG, WebP, or GIF image. Max size: 5MB. Recommended: 400x400px
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                            Save the vendor first to enable photo uploads.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Profile Slug (URL)</Label>
                      <Input
                        value={formData.profile_settings?.profile_slug || ''}
                        onChange={(e) => updateProfileSettings('profile_slug', e.target.value)}
                        placeholder="ana-martinez"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tagline</Label>
                      <Input
                        value={formData.profile_settings?.tagline || ''}
                        onChange={(e) => updateProfileSettings('tagline', e.target.value)}
                        placeholder="Professional Medical Interpreter"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bio</Label>
                    <Textarea
                      value={formData.profile_settings?.bio || ''}
                      onChange={(e) => updateProfileSettings('bio', e.target.value)}
                      placeholder="A brief description about the interpreter..."
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={formData.profile_settings?.city || ''}
                        onChange={(e) => updateProfileSettings('city', e.target.value)}
                        placeholder="Milwaukee, WI"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Years of Experience</Label>
                      <Input
                        type="number"
                        value={formData.profile_settings?.years_experience || ''}
                        onChange={(e) => updateProfileSettings('years_experience', parseInt(e.target.value) || null)}
                        placeholder="5"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Availability</Label>
                      <Input
                        value={formData.profile_settings?.availability || ''}
                        onChange={(e) => updateProfileSettings('availability', e.target.value)}
                        placeholder="Mon-Fri, 9AM-5PM"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Favorite Assignments</Label>
                    <Input
                      value={formData.profile_settings?.favorite_assignments || ''}
                      onChange={(e) => updateProfileSettings('favorite_assignments', e.target.value)}
                      placeholder="Medical appointments, Legal consultations"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Custom Message</Label>
                    <Input
                      value={formData.profile_settings?.custom_message || ''}
                      onChange={(e) => updateProfileSettings('custom_message', e.target.value)}
                      placeholder="Looking forward to helping bridge communication gaps!"
                    />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="show_languages"
                        checked={formData.profile_settings?.show_languages !== false}
                        onCheckedChange={(checked) => updateProfileSettings('show_languages', checked)}
                      />
                      <Label htmlFor="show_languages" className="text-sm">Show Languages</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="show_specialties"
                        checked={formData.profile_settings?.show_specialties !== false}
                        onCheckedChange={(checked) => updateProfileSettings('show_specialties', checked)}
                      />
                      <Label htmlFor="show_specialties" className="text-sm">Show Specialties</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="show_credentials"
                        checked={formData.profile_settings?.show_credentials || false}
                        onCheckedChange={(checked) => updateProfileSettings('show_credentials', checked)}
                      />
                      <Label htmlFor="show_credentials" className="text-sm">Show Credentials</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="show_modalities"
                        checked={formData.profile_settings?.show_modalities !== false}
                        onCheckedChange={(checked) => updateProfileSettings('show_modalities', checked)}
                      />
                      <Label htmlFor="show_modalities" className="text-sm">Show Service Types</Label>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Vendor Documents Section - Only show for existing vendors */}
        {isEdit && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Vendor Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {DOCUMENT_TYPES.map((docType) => {
                  const doc = formData.documents?.[docType.key];
                  const isUploading = uploadingDocument === docType.key;
                  
                  return (
                    <div 
                      key={docType.key}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        doc ? 'border-green-200 bg-green-50' : 'border-dashed border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className={`h-5 w-5 ${doc ? 'text-green-600' : 'text-gray-400'}`} />
                          <span className="font-medium text-sm">{docType.label}</span>
                          {docType.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                        </div>
                        {doc && <CheckCircle className="h-5 w-5 text-green-600" />}
                      </div>
                      
                      {doc ? (
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground truncate">{doc.filename}</p>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDocument(doc.url, doc.filename)}
                              className="flex-1"
                            >
                              <Download className="h-3 w-3 mr-1" /> View
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => updateDocument(docType.key, null)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            id={`doc-${docType.key}`}
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => handleDocumentUpload(docType.key, e.target.files?.[0])}
                            disabled={isUploading}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById(`doc-${docType.key}`).click()}
                            disabled={isUploading}
                            className="w-full"
                          >
                            {isUploading ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                            ) : (
                              <><Upload className="h-4 w-4 mr-2" /> Upload</>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ID Badge Section - Only show for existing vendors */}
        {isEdit && (
          <Card className="border-blue-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="flex items-center gap-2">
                <QrCode className="h-5 w-5 text-blue-600" />
                Bylyngo Interpreter ID Badge
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Profile Photo Upload */}
              <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Profile Photo
                </h4>
                <div className="flex items-center gap-4">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-full border-4 border-blue-200 overflow-hidden bg-gray-100">
                      {formData.profile_settings?.profile_photo_url ? (
                        <img 
                          src={getPhotoUrl(formData.profile_settings.profile_photo_url)} 
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Camera className="h-8 w-8" />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      ) : (
                        <Camera className="h-6 w-6 text-white" />
                      )}
                    </button>
                  </div>
                  <div>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                    >
                      {uploadingPhoto ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Photo
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPEG, PNG, GIF or WebP. Max 2MB.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Badge Preview - New vertical design */}
                <div 
                  className="w-[2.125in] h-[3.375in] rounded-2xl relative overflow-hidden shadow-xl flex-shrink-0"
                  id="id-badge-preview"
                  style={{ background: '#0027A9' }}
                >
                  {/* Decorative curve */}
                  <div 
                    className="absolute"
                    style={{
                      top: '50%',
                      left: '-20%',
                      width: '140%',
                      height: '60%',
                      background: '#FFD700',
                      borderRadius: '50%',
                      transform: 'rotate(-15deg)'
                    }}
                  />
                  
                  {/* Orange accent */}
                  <div 
                    className="absolute w-14 h-14 rounded-full"
                    style={{ 
                      top: '35%', 
                      right: '-10%', 
                      background: '#FF6600' 
                    }}
                  />
                  
                  {/* Logo */}
                  <div className="absolute top-4 left-4 z-10">
                    <div 
                      className="w-8 h-8 rounded-md flex items-center justify-center font-extrabold text-lg mb-1"
                      style={{ background: '#FFD700', color: '#0027A9' }}
                    >
                      B
                    </div>
                    <div className="text-sm font-bold" style={{ color: '#FFD700' }}>Bylyngo</div>
                    <div className="text-[8px] text-white uppercase tracking-wider">Interpreting</div>
                  </div>
                  
                  {/* ID Number */}
                  <div 
                    className="absolute top-4 right-2 px-2 py-2 rounded-xl font-bold text-[10px] z-10"
                    style={{ 
                      background: '#FFD700', 
                      color: '#0027A9',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed'
                    }}
                  >
                    {formData.vendor_number || 'V-XXXXX'}
                  </div>
                  
                  {/* Photo */}
                  <div className="absolute top-20 right-4 z-10">
                    <div 
                      className="w-20 h-20 rounded-full overflow-hidden border-4 bg-gray-200"
                      style={{ borderColor: '#FFD700' }}
                    >
                      {formData.profile_settings?.profile_photo_url ? (
                        <img 
                          src={getPhotoUrl(formData.profile_settings.profile_photo_url)} 
                          alt="Photo"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100">
                          <Camera className="h-6 w-6" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Name & Title */}
                  <div className="absolute left-4 top-24 z-10 max-w-[50%]">
                    <div 
                      className="font-extrabold uppercase leading-tight text-sm"
                      style={{ color: '#FFD700' }}
                    >
                      {formData.first_name || 'Interpreter'}
                    </div>
                    {formData.last_name && (
                      <div 
                        className="font-bold uppercase text-xs"
                        style={{ color: '#FFD700' }}
                      >
                        {formData.last_name}
                      </div>
                    )}
                    <div 
                      className="text-[9px] font-semibold uppercase tracking-wider mt-1"
                      style={{ color: '#FFD700' }}
                    >
                      Certified Interpreter
                    </div>
                  </div>
                  
                  {/* QR Code */}
                  <div className="absolute bottom-14 right-4 text-center z-10">
                    <div className="bg-white p-1 rounded-md">
                      <QRCodeSVG 
                        value={getPublicProfileUrl()} 
                        size={40}
                        level="M"
                        includeMargin={false}
                      />
                    </div>
                    <div className="text-[7px] text-white mt-1">Scan to verify</div>
                  </div>
                  
                  {/* Bottom bar */}
                  <div 
                    className="absolute bottom-0 left-0 right-0 px-4 py-3 z-10"
                    style={{ background: '#FFD700' }}
                  >
                    <div 
                      className="font-extrabold text-xs uppercase"
                      style={{ color: '#0027A9' }}
                    >
                      Bylyngo Interpreting
                    </div>
                    <div className="text-[8px] text-white">www.bylyngo.com</div>
                  </div>
                </div>
                
                {/* Badge Actions */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Interpreter ID Badge</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      This professional ID badge can be printed and worn during assignments. 
                      The QR code links to the interpreter's verified public profile.
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={handlePrintBadge}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Printer className="h-4 w-4 mr-2" /> Print Badge
                    </Button>
                    
                    {formData.profile_settings?.public_profile_enabled && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => window.open(getPublicProfileUrl(), '_blank')}
                      >
                        <Globe className="h-4 w-4 mr-2" /> View Public Profile
                      </Button>
                    )}
                  </div>
                  
                  {!formData.profile_settings?.profile_photo_url && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                      <span className="text-sm text-amber-700">Upload a photo above to complete the ID badge</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent & Pending Jobs - Only show for existing vendors */}
        {isEdit && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Recent & Pending Jobs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentJobs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No recent assignments for this vendor</p>
                  <p className="text-xs mt-1">Jobs will appear here once assigned</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {recentJobs.slice(0, 5).map((job, idx) => (
                      <div 
                        key={job.assignment_id || idx}
                        className="flex items-center gap-4 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                      >
                        <div className={`w-2 h-2 rounded-full ${
                          job.status === 'completed' ? 'bg-green-500' :
                          job.status === 'confirmed' ? 'bg-blue-500' :
                          job.status === 'pending' ? 'bg-amber-500' :
                          'bg-gray-400'
                        }`} />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-blue-600">
                              {job.work_order_id}
                            </span>
                            <Badge variant="outline" className="text-xs capitalize">
                              {job.status?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground truncate">
                            {job.client_name}
                          </div>
                        </div>
                        
                        <div className="text-right text-sm">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {job.scheduled_date ? new Date(job.scheduled_date).toLocaleDateString() : 'TBD'}
                          </div>
                          {job.location && (
                            <div className="flex items-center gap-1 text-muted-foreground text-xs">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate max-w-[120px]">{job.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {recentJobs.length > 5 && (
                    <Button
                      type="button"
                      variant="link"
                      className="mt-4 w-full"
                      onClick={() => navigate(`/vendors/${vendorId}/assignments`)}
                    >
                      View all {recentJobs.length} assignments →
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate('/vendors')}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading} data-testid="save-vendor-btn">
            {loading ? 'Saving...' : (isEdit ? 'Update Vendor' : 'Create Vendor')}
          </Button>
        </div>
      </form>

      {/* Document Viewer Modal */}
      {viewerOpen && viewerUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={closeViewer}>
          <div className="relative bg-white rounded-lg shadow-2xl w-[90vw] h-[90vh] max-w-6xl" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-lg">
              <h3 className="font-semibold text-lg truncate flex-1 mr-4">{viewerFilename}</h3>
              <div className="flex items-center gap-2">
                <a 
                  href={viewerUrl} 
                  download={viewerFilename}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <Download className="h-4 w-4" />
                  Download
                </a>
                <button 
                  onClick={closeViewer}
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="h-[calc(90vh-80px)] overflow-auto bg-gray-100 rounded-b-lg">
              {viewerType === 'pdf' ? (
                <iframe 
                  src={viewerUrl} 
                  className="w-full h-full border-0"
                  title={viewerFilename}
                />
              ) : (
                <div className="flex items-center justify-center h-full p-4">
                  <img 
                    src={viewerUrl} 
                    alt={viewerFilename}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorForm;
